/**
 * Super Admin — create a user account and place it in a workspace.
 *
 * Two calls, deliberately not one: /api/admin/create-user owns the account
 * (it needs service_role), and admin_set_workspace_role owns the grant (the
 * only path left after the INSERT policies on workspace_members were dropped).
 * They are not atomic, so the grant failing after the account exists is a real
 * state the master must be told about plainly — see handleSubmit.
 *
 * @module components/admin/AddUserModal
 */

import React, { useState } from "react";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { createUser, setWorkspaceRole, type AdminWorkspace } from "../../services/adminService";
import { MIN_PASSWORD_LENGTH } from "@/config/auth";
import type { WorkspaceRole } from "../../types";

const ROLES: WorkspaceRole[] = ["admin", "analyst", "viewer"];

export interface AddUserModalProps {
  isOpen: boolean;
  workspaces: AdminWorkspace[];
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  workspaces,
  onClose,
  onCreated,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [workspaceId, setWorkspaceId] = useState(workspaces[0]?.id ?? "");
  const [role, setRole] = useState<WorkspaceRole>("viewer");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setEmail("");
    setPassword("");
    setWorkspaceId(workspaces[0]?.id ?? "");
    setRole("viewer");
    setError(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Enter a valid email address");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }

    setBusy(true);
    const created = await createUser(email, password);
    if (!created.success || !created.data) {
      setError(created.error ?? "Could not create the user");
      setBusy(false);
      return;
    }

    if (workspaceId) {
      const granted = await setWorkspaceRole(created.data.id, workspaceId, role);
      if (!granted.success) {
        // The account exists. Saying "failed" would push the master into a
        // retry that can only hit "email already registered" from here on.
        await onCreated();
        setError(
          `The account was created, but granting workspace access failed: ${
            granted.error ?? "unknown error"
          }. Use Manage access on their row to finish.`
        );
        setBusy(false);
        return;
      }
    }

    await onCreated();
    setBusy(false);
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add user"
      description="Creates the account immediately. Send the password to them yourself."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => void handleSubmit()} loading={busy} disabled={busy}>
            Create user
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2 text-sm">
            {error}
          </div>
        )}

        <label className="block text-sm">
          <span className="text-neutral-700">Email</span>
          <input
            type="email"
            aria-label="Email"
            value={email}
            disabled={busy}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full text-sm border border-neutral-300 rounded-md px-3 py-1.5"
          />
        </label>

        <label className="block text-sm">
          <span className="text-neutral-700">Password</span>
          <input
            type="text"
            aria-label="Password"
            value={password}
            disabled={busy}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full text-sm border border-neutral-300 rounded-md px-3 py-1.5"
          />
          <span className="text-xs text-neutral-700">
            Minimum {MIN_PASSWORD_LENGTH} characters. Shown in clear text so you can copy it — no email is sent.
          </span>
        </label>

        <label className="block text-sm">
          <span className="text-neutral-700">Workspace</span>
          <select
            aria-label="Workspace"
            value={workspaceId}
            disabled={busy}
            onChange={(e) => setWorkspaceId(e.target.value)}
            className="mt-1 w-full text-sm border border-neutral-300 rounded-md px-3 py-1.5"
          >
            <option value="">No workspace yet</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="text-neutral-700">Role</span>
          <select
            aria-label="Role"
            value={role}
            disabled={busy || !workspaceId}
            onChange={(e) => setRole(e.target.value as WorkspaceRole)}
            className="mt-1 w-full text-sm border border-neutral-300 rounded-md px-3 py-1.5"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>
    </Modal>
  );
};

export default AddUserModal;
